import * as Scrivito from "scrivito";
import React, { useCallback, useMemo } from "react";
import { Tree } from "@hogartrabe/react-arborist-vrh";
import useResizeObserver from "use-resize-observer";
import { useAppBaseContext } from "providers";
import { useProjectDataStore, useProjectUIStore } from "stores/projectStore";
import {
  useFetchProject,
  useNavigateToCustomProduct,
  useNavigateToProduct,
  useNodeActions,
} from "hooks/projectHooks";
import { XCircleLoader } from "components/XCircleLoader/XCircleLoader";
import { useProjectTreeContentTranslations } from "./hooks/useProjectTreeContentTranslations";
import { Node } from "./components/Node";
import { disableDrop, isDragDisabled } from "./utils";
import { defaultOptions } from "./defaultOptions";
import {
  useCalculateRowHeight,
  useRedraw,
  useSmoothScroll,
  useSyncProjectId,
} from "./hooks";
import { DraggedFolder } from "./components/Folder/DraggedFolder";
import { useRedirectOnError } from "./hooks/useRedirectOnError";

export const ProjectTree = Scrivito.connect(({ options = {} }) => {
  const treeRef = useProjectDataStore((state) => state.treeRef);
  const projectTree = useProjectDataStore((state) => state.projectTree);
  const isLoading = useProjectDataStore((state) => state.isLoading);
  const openMap = useProjectUIStore((state) => state.getOpenMap());

  useRedirectOnError();

  const { lang } = useAppBaseContext();
  // Translations needed for the project tree, fetched here instead of in the Node component to avoid unnecessary translate calls.
  const translations = useProjectTreeContentTranslations();
  // Default options for the project tree, which can be overridden by the options prop.
  const treeOptions = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options],
  );

  const refetchProject = useFetchProject();
  const navigateToProduct = useNavigateToProduct();
  const navigateToCustomProduct = useNavigateToCustomProduct();
  // resize observer for providing the correct width and height to the tree component.
  const { ref, width, height } = useResizeObserver();

  // Custom hook for animating the smooth scroll-to behaviour
  const { setScrollToIndex, setScrollOffset } = useSmoothScroll({
    windowHeight: height,
  });
  // Custom hook for node actions, which provides the actions for the nodes in the project tree.
  const nodeActions = useNodeActions();
  // This custom hook ensures that the project tree is in sync with the projectId in the URL.
  useSyncProjectId();
  // This custom hook sets the copied item in the project tree context when the copiedItemArg is passed.

  // Function for ensuring that the node heights are correct after the project tree is updated.
  // It redraws the list, recalculating the heights of the nodes from the index that is passed to it.
  const { setRedrawIndex } = useRedraw();

  // Tree props
  const onMove = useCallback(
    (args) => nodeActions.moveNode(args, setRedrawIndex),
    [nodeActions, setRedrawIndex],
  );
  const childrenAccessor = useCallback((d) => d.nodes, []);
  const calculateRowHeight = useCalculateRowHeight(treeOptions);
  const rowHeight = useCallback(
    (node) => calculateRowHeight(node),
    [calculateRowHeight],
  );

  const RenderNode = useCallback(
    (props) => (
      <Node
        {...props}
        options={treeOptions}
        nodeActions={nodeActions}
        translations={translations}
        setRedrawIndex={setRedrawIndex}
        setScrollToIndex={setScrollToIndex}
        navigateToProduct={navigateToProduct}
        fetchProjectData={refetchProject}
        lang={lang}
        navigateToCustomProduct={navigateToCustomProduct}
      />
    ),
    [
      lang,
      navigateToCustomProduct,
      navigateToProduct,
      nodeActions,
      refetchProject,
      setRedrawIndex,
      setScrollToIndex,
      translations,
      treeOptions,
    ],
  );

  const RenderDragPreview = useCallback(
    (props) => {
      if (!props.id || !props.mouse || !props.isDragging) {
        return null;
      }

      const draggedNode = treeRef.current?.get?.(props.id);
      const draggedData = draggedNode?.data;
      if (!draggedNode || !draggedData) {
        return null;
      }

      return (
        <div
          style={{
            left: props.mouse.x + 8,
            top: props.mouse.y - 50,
          }}
          className="position-fixed z-index-9999 w-max-content"
        >
          <DraggedFolder node={draggedNode} />
        </div>
      );
    },
    [treeRef],
  );

  const disableDrag = useMemo(
    () => (treeOptions.dndDisabled ? true : (node) => isDragDisabled(node)),
    [treeOptions.dndDisabled],
  );

  const onScroll = useCallback(
    ({ scrollOffset }) => setScrollOffset(scrollOffset),
    [setScrollOffset],
  );

  // This boolean is used to determine if the project tree should be shown.

  const showTree = projectTree && openMap && height && width;

  return (
    <div className="w-100 h-100 pb-4" ref={ref}>
      {isLoading && (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <XCircleLoader />
        </div>
      )}

      {showTree && !isLoading && (
        <Tree
          data={projectTree}
          initialOpenState={openMap}
          childrenAccessor={childrenAccessor}
          onMove={onMove}
          indent={treeOptions.indentSize}
          width={width}
          height={height}
          ref={treeRef}
          rowHeight={rowHeight}
          disableDrag={disableDrag}
          renderDragPreview={RenderDragPreview}
          disableDrop={disableDrop}
          overscanCount={treeOptions.overscanCount}
          onScroll={onScroll}
        >
          {RenderNode}
        </Tree>
      )}
    </div>
  );
});
